import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MonitoringBoot } from "./MonitoringBoot";
import { getSettings, getStatus } from "../lib/safegeo-api";
import { isFreePlan } from "../lib/format";
import { queryKeys } from "../lib/query-keys";
import { screenTitleForPath } from "../lib/page-title";
import { useAppBridgeRouterSync } from "../lib/app-nav";
import { Sentry, setSentryScreen, setSentryShopContext } from "../lib/sentry";
import { AppLink } from "../components/AppLink";
import { AppButton } from "../components/AppButton";
import { SafePage } from "../components/SafePage";
import { PageLoading } from "../components/PageLoading";
import { WarningBanner } from "../components/PageBanner";
import { UnlockModals } from "../components/UnlockModals";

const APP_ID = import.meta.env.VITE_SHOPIFY_API_KEY || "";

/** App Bridge idToken only exists inside Shopify Admin iframe - wait briefly for boot. */
function useEmbeddedReady(): "loading" | "embedded" | "standalone" {
  const [state, setState] = useState<"loading" | "embedded" | "standalone">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    const started = Date.now();
    const tick = () => {
      if (cancelled) return;
      try {
        if (typeof window.shopify?.idToken === "function") {
          setState("embedded");
          return;
        }
      } catch {
        /* ignore */
      }
      if (Date.now() - started < 2500) {
        window.setTimeout(tick, 100);
        return;
      }
      setState("standalone");
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * v5 nav (SafeGeo_Dev_Spec §3): Home · Market · Store · Block · Popup · Settings · Plan · Help.
 * Plan stays on the menu (with plan chip). First-pick still uses /app/plans but nav stays
 * hidden only on Welcome.
 */
export function AppShell() {
  const location = useLocation();
  useAppBridgeRouterSync();
  const embedState = useEmbeddedReady();

  useEffect(() => {
    document.title = screenTitleForPath(location.pathname);
    setSentryScreen(location.pathname);
  }, [location.pathname]);

  const statusQ = useQuery({
    queryKey: queryKeys.status,
    queryFn: getStatus,
    enabled: embedState === "embedded",
    staleTime: 60_000,
  });

  /** Refreshes shop contact from Shopify when missing - feeds Crisp identity. */
  const settingsQ = useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
    enabled: embedState === "embedded",
    staleTime: 5 * 60_000,
  });

  const shop = statusQ.data?.shop_domain || null;
  const plan = statusQ.data?.plan || statusQ.data?.plan_key || null;
  const contactEmail =
    statusQ.data?.contact_email || settingsQ.data?.contact_email || null;
  const contactName =
    statusQ.data?.contact_name ||
    settingsQ.data?.contact_name ||
    shop?.replace(/\.myshopify\.com$/i, "") ||
    null;
  const free = isFreePlan(statusQ.data?.plan, statusQ.data?.plan_key);
  const multiUnlocked = !!statusQ.data?.multi_store_unlocked;
  const needsPlanPick = !!statusQ.data?.needs_plan_pick;
  const onFirstPickPlans =
    location.pathname === "/app/plans" ||
    location.pathname === "/app/plan" ||
    location.pathname === "/plans";

  /** Welcome + first plan pick only. Billing (/app/billing) keeps the v5 Plan nav item. */
  const hideNav =
    location.pathname === "/app/welcome" ||
    location.pathname === "/welcome" ||
    (onFirstPickPlans && (needsPlanPick || statusQ.isLoading));

  useEffect(() => {
    if (!statusQ.data) return;
    setSentryShopContext({ shop, plan });
  }, [statusQ.data, shop, plan]);

  if (embedState === "loading") {
    return (
      <SafePage screen="home" heading="SafeGeo">
        <PageLoading label="Connecting to Shopify Admin…" />
      </SafePage>
    );
  }

  if (embedState === "standalone") {
    return (
      <SafePage screen="home" heading="SafeGeo">
        <WarningBanner heading="Open SafeGeo from Shopify Admin">
          <s-paragraph>
            This app runs inside Shopify Admin (Apps → SafeGeo). Opening the
            URL directly in a new tab won’t work.
          </s-paragraph>
          <s-paragraph color="subdued">
            Partner App URL should be{" "}
            <s-text type="strong">https://safegeo.getpickads.com/app</s-text>
            .
          </s-paragraph>
        </WarningBanner>
        {import.meta.env.VITE_SENTRY_DSN ? (
          <s-box paddingBlockStart="base">
            <AppButton
              variant="tertiary"
              onClick={() => {
                Sentry.captureException(
                  new Error("SafeGeo Admin Sentry verification error"),
                );
                void Sentry.flush(2000);
              }}
            >
              Send Sentry test error
            </AppButton>
          </s-box>
        ) : null}
      </SafePage>
    );
  }

  const storeNavLabel = free
    ? "Store redirect · Starter+"
    : !multiUnlocked
      ? "Store redirect · Multi+"
      : "Store redirect";
  const blockNavLabel = free ? "Block country · Starter+" : "Block country";
  /** v7 §28: plan chip only on Plan title bar - nav stays flat "Plan". */
  const planNavLabel = "Plan";

  return (
    <>
      <MonitoringBoot
        shop={shop}
        plan={plan}
        appId={APP_ID || null}
        email={contactEmail}
        name={contactName}
      />
      {!hideNav ? (
        <s-app-nav>
          <AppLink to="/app" rel="home">
            Home
          </AppLink>
          <AppLink to="/app/market">Market redirect</AppLink>
          <AppLink to="/app/stores">{storeNavLabel}</AppLink>
          <AppLink to="/app/block">{blockNavLabel}</AppLink>
          <AppLink to="/app/popup">Popup</AppLink>
          <AppLink to="/app/settings">Settings</AppLink>
          <AppLink to="/app/billing">{planNavLabel}</AppLink>
          <AppLink to="/app/help">Help</AppLink>
        </s-app-nav>
      ) : null}
      <Outlet />
      <UnlockModals />
    </>
  );
}
